import React, { useEffect, useState } from 'react';
import { Menu } from '@mantine/core';
import type { PortalProps } from '@mantine/core';
import { IconMovie, IconCheck } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import * as modelViewer from '@google/model-viewer';
import { useBrandForeground } from '@/utils/brandForeground';
import {
  VIEWER_CONTROL_CHROME_HOVER_BG,
  VIEWER_CONTROL_CHROME_STYLE,
} from '@/components/ModelViewer/viewerControlChrome';
import { animationNamesEqual } from '@/utils/modelViewerAnimation';

interface AnimationSelectorProps {
  modelViewerRef: React.RefObject<modelViewer.ModelViewerElement | null>;
  color?: string;
  /** When the viewer is in theater or fullscreen, pass the same base as AnnotationNavigator (e.g. 2100) for portaled menus. */
  immersiveLayerBaseZ?: number;
  menuPortalProps?: Omit<PortalProps, 'children'>;
}

/**
 * Animation selector component for model-viewer
 * Displays a small button that opens a menu to select from available GLB animations
 * Similar style to annotation hotspot buttons
 */
export function AnimationSelector({
  modelViewerRef,
  color = '#5D7723',
  immersiveLayerBaseZ,
  menuPortalProps,
}: AnimationSelectorProps) {
  const { t } = useTranslation();
  const iconColor = useBrandForeground(color);
  const [availableAnimations, setAvailableAnimations] = useState<string[]>([]);
  const [currentAnimation, setCurrentAnimation] = useState<string | null>(null);

  useEffect(() => {
    const modelViewer = modelViewerRef.current;
    if (!modelViewer) return;

    const updateAnimations = () => {
      try {
        // Get available animations from model-viewer
        // availableAnimations is a getter property that returns an array
        const animations = (modelViewer as any).availableAnimations || [];
        
        // Filter out empty/null animations and ensure we have valid names
        const validAnimations = animations.filter((name: string) => name && name.trim() !== '');

        setAvailableAnimations((current) =>
          animationNamesEqual(current, validAnimations) ? current : validAnimations
        );

        // Get current animation
        const current = (modelViewer as any).animationName || null;
        setCurrentAnimation((previous) => (previous === current ? previous : current));
      } catch (error) {
        console.error('Error getting animations:', error);
      }
    };

    // Update animations when model loads
    const handleLoad = () => {
      // Small delay to ensure animations are loaded
      setTimeout(updateAnimations, 100);
    };

    modelViewer.addEventListener('load', handleLoad);
    
    // Also check periodically in case animations load after the load event
    const interval = setInterval(() => {
      if (modelViewerRef.current) {
        updateAnimations();
      }
    }, 500);

    // Initial check
    updateAnimations();

    return () => {
      modelViewer.removeEventListener('load', handleLoad);
      clearInterval(interval);
    };
  }, [modelViewerRef]);

  const handleAnimationSelect = (animationName: string) => {
    const modelViewer = modelViewerRef.current;
    if (!modelViewer) return;

    try {
      // Set the selected animation
      // animationName is a settable property
      (modelViewer as any).animationName = animationName;
      setCurrentAnimation(animationName);
      
      // Play the animation
      modelViewer.play();
    } catch (error) {
      console.error('Error setting animation:', error);
    }
  };

  // Don't render if no animations available
  if (availableAnimations.length === 0) {
    return null;
  }

  const menuPortalZ = immersiveLayerBaseZ != null ? immersiveLayerBaseZ + 600 : 1001;
  const targetButtonZ = immersiveLayerBaseZ != null ? immersiveLayerBaseZ + 45 : 11;

  return (
    <Menu
      shadow="md"
      width={200}
      position="top-end"
      zIndex={menuPortalZ}
      withinPortal
      portalProps={menuPortalProps}
    >
      <Menu.Target>
        <button
          style={{
            ...VIEWER_CONTROL_CHROME_STYLE,
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease',
            cursor: 'pointer',
            padding: 0,
            color: iconColor,
            fontSize: '0.875rem',
            fontWeight: 500,
            transform: 'scale(1)',
            position: 'relative',
            zIndex: targetButtonZ,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.backgroundColor = VIEWER_CONTROL_CHROME_HOVER_BG;
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.backgroundColor = VIEWER_CONTROL_CHROME_STYLE.backgroundColor;
            e.currentTarget.style.boxShadow = VIEWER_CONTROL_CHROME_STYLE.boxShadow;
          }}
          onMouseDown={(e) => {
            e.currentTarget.style.transform = 'scale(0.95)';
          }}
          onMouseUp={(e) => {
            e.currentTarget.style.transform = 'scale(1.05)';
          }}
          className="desktop-ar-buttons"
          title={t('productViewer.selectAnimation')}
        >
          <IconMovie size={20} />
        </button>
      </Menu.Target>

      <Menu.Dropdown
        style={{
          maxHeight: '400px',
          overflowY: 'auto',
        }}
      >
        <Menu.Label>{t('productViewer.selectAnimation')}</Menu.Label>
        {availableAnimations.map((animationName) => (
          <Menu.Item
            key={animationName}
            leftSection={<IconMovie size={16} />}
            rightSection={
              currentAnimation === animationName ? <IconCheck size={14} /> : null
            }
            onClick={() => handleAnimationSelect(animationName)}
            style={
              currentAnimation === animationName
                ? {
                    backgroundColor: `${color}15`,
                    color: iconColor,
                    fontWeight: 600,
                  }
                : {}
            }
          >
            <span
              style={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '150px',
              }}
              title={animationName || 'Default'}
            >
              {animationName || 'Default'}
            </span>
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}
